import { useEffect } from "react";
import { useLanguage, useT } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/en.ts";
import { localeFor } from "../i18n/language.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import {
  type ResultVariableKey,
  unitLabelFor,
  variablesFor,
} from "../model/results/resultsVariables.ts";
import { heatColor } from "./resultsAnimationColors.ts";
import { PlayIcon } from "./PlayIcon.tsx";
import { PauseIcon } from "./PauseIcon.tsx";

const STEP_MS = 400; // Fast enough to feel animated, slow enough to actually read the map.
const GRADIENT = `linear-gradient(to right, ${heatColor(0)}, ${heatColor(0.25)}, ${heatColor(0.5)}, ${heatColor(0.75)}, ${heatColor(1)})`;

const CATEGORY_LABEL_KEY: Record<LegendCategory, TextKey> = {
  node: "elementBrowser.nodes",
  link: "elementBrowser.links",
  subcatchment: "elementBrowser.subcatchments",
};

type LegendCategory = "node" | "link" | "subcatchment";

/**
 * One row of the legend panel: a checkbox for this category's color on the
 * map, a variable picker, and a gradient legend re-scaled to the chosen
 * variable's peak every time it changes. Hidden entirely when this project
 * has none of the category.
 */
function LegendRow({
  category,
  variable,
  onVariableChange,
  peak,
  flowUnits,
  count,
  checked,
  onChange,
}: {
  category: LegendCategory;
  variable: ResultVariableKey;
  onVariableChange: (key: ResultVariableKey) => void;
  peak: number;
  flowUnits: string;
  count: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const t = useT();
  const language = useLanguage();
  if (count === 0) return null;
  const unit = unitLabelFor(variable, flowUnits);
  const kindLabel = t(CATEGORY_LABEL_KEY[category]);
  return (
    <label className="results-animation-legend-row">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={t("resultsAnimation.showColor", { kind: kindLabel })}
      />
      <select
        className="results-animation-legend-select"
        value={variable}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onVariableChange(e.target.value as ResultVariableKey)}
        aria-label={t("resultsAnimation.variablePicker", { kind: kindLabel })}
      >
        {variablesFor(category).map((v) => (
          <option key={v.key} value={v.key}>
            {t(v.labelKey)}
          </option>
        ))}
      </select>
      <div className="thematic-map-legend">
        <span>0</span>
        <div className="thematic-map-gradient" style={{ background: GRADIENT }} />
        <span>
          {formatNumber(peak, language, 2)} {unit}
        </span>
      </div>
    </label>
  );
}

/**
 * Floating controls over the map: play/pause + a scrubbable slider over
 * the report timestamps. The color legend is a separate panel, kept apart
 * from the compact play controls so neither gets cramped. Owns the
 * "advance one frame" timer itself (a plain `setInterval`, not
 * `requestAnimationFrame` — this is a discrete step through `results.times`,
 * not a smooth-motion animation).
 */
export function ResultsAnimationBar({
  times,
  index,
  onIndexChange,
  playing,
  onTogglePlay,
  onClose,
  nodeCount,
  linkCount,
  subcatchmentCount,
  nodeVariablePeak,
  linkVariablePeak,
  subcatchmentVariablePeak,
  flowUnits,
  showNodes,
  onShowNodesChange,
  showLinks,
  onShowLinksChange,
  showSubcatchments,
  onShowSubcatchmentsChange,
  nodeVariable,
  onNodeVariableChange,
  linkVariable,
  onLinkVariableChange,
  subcatchmentVariable,
  onSubcatchmentVariableChange,
}: {
  times: Date[];
  index: number;
  onIndexChange: (index: number) => void;
  playing: boolean;
  onTogglePlay: () => void;
  onClose: () => void;
  nodeCount: number;
  linkCount: number;
  subcatchmentCount: number;
  /** The global peak (every node/every timestamp) this category's color scale is normalized against, for whichever variable is currently chosen — so the legend shows the exact number the color scale means. */
  nodeVariablePeak: number;
  linkVariablePeak: number;
  subcatchmentVariablePeak: number;
  flowUnits: string;
  /** Per-category color toggle — a subcatchment polygon's 45% fill, the biggest of the three, would otherwise wash over nodes/links underneath. Never hides the elements themselves — that's ElementBrowser.tsx's own eye toggle. */
  showNodes: boolean;
  onShowNodesChange: (show: boolean) => void;
  showLinks: boolean;
  onShowLinksChange: (show: boolean) => void;
  showSubcatchments: boolean;
  onShowSubcatchmentsChange: (show: boolean) => void;
  nodeVariable: ResultVariableKey;
  onNodeVariableChange: (key: ResultVariableKey) => void;
  linkVariable: ResultVariableKey;
  onLinkVariableChange: (key: ResultVariableKey) => void;
  subcatchmentVariable: ResultVariableKey;
  onSubcatchmentVariableChange: (key: ResultVariableKey) => void;
}) {
  const t = useT();
  const language = useLanguage();

  useEffect(() => {
    if (!playing || times.length === 0) return;
    const id = setInterval(() => {
      onIndexChange((index + 1) % times.length);
    }, STEP_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arms every frame on purpose, reading the latest index each time rather than closing over a stale one.
  }, [playing, index, times.length]);

  const current = times[index];
  const label = current
    ? current.toLocaleString(localeFor(language), {
        month: "short", // Not "numeric" — "1/6" reads as ambiguous.
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC", // Simulation timestamps are UTC-anchored; display must match.
      })
    : "";

  return (
    <>
      <div className="results-animation-bar">
        <button
          className="button topbar-icon-button"
          onClick={onTogglePlay}
          aria-label={playing ? t("resultsAnimation.pause") : t("resultsAnimation.play")}
          title={playing ? t("resultsAnimation.pause") : t("resultsAnimation.play")}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(times.length - 1, 0)}
          value={index}
          onChange={(e) => onIndexChange(Number(e.target.value))}
          className="results-animation-slider"
          aria-label={t("resultsAnimation.timeline")}
        />
        <span className="results-animation-label">{label}</span>
        <button className="button" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
      <div className="results-animation-legend-panel">
        <LegendRow
          category="node"
          variable={nodeVariable}
          onVariableChange={onNodeVariableChange}
          peak={nodeVariablePeak}
          flowUnits={flowUnits}
          count={nodeCount}
          checked={showNodes}
          onChange={onShowNodesChange}
        />
        <LegendRow
          category="link"
          variable={linkVariable}
          onVariableChange={onLinkVariableChange}
          peak={linkVariablePeak}
          flowUnits={flowUnits}
          count={linkCount}
          checked={showLinks}
          onChange={onShowLinksChange}
        />
        <LegendRow
          category="subcatchment"
          variable={subcatchmentVariable}
          onVariableChange={onSubcatchmentVariableChange}
          peak={subcatchmentVariablePeak}
          flowUnits={flowUnits}
          count={subcatchmentCount}
          checked={showSubcatchments}
          onChange={onShowSubcatchmentsChange}
        />
      </div>
    </>
  );
}
